import React, { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useProgrammerContext } from '@/hid/ProgrammerContext';
import { FlashEngine8051 } from '@/flash/FlashEngine8051';
import { parseIntelHex } from '@/flash/parsers/HexParser';
import { parseSn8 } from '@/flash/parsers/Sn8Parser';
import type { FlashProgress8051, FlashResult8051 } from '@/types/hid8051';

type Operation = 'idle' | 'flashing' | 'reading' | 'erasing' | 'verifying';

const engine = new FlashEngine8051();

const FlashControls8051: React.FC = () => {
  const { t } = useTranslation('webflash');
  const { programmer, selectedChip, status } = useProgrammerContext();
  const [firmwareFile, setFirmwareFile] = useState<File | null>(null);
  const [firmwareData, setFirmwareData] = useState<Uint8Array | null>(null);
  const [operation, setOperation] = useState<Operation>('idle');
  const [progress, setProgress] = useState<FlashProgress8051 | null>(null);
  const [result, setResult] = useState<FlashResult8051 | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [readData, setReadData] = useState<Uint8Array | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isReady = status === 'connected' && programmer && selectedChip;
  const isBusy = operation !== 'idle';

  const loadFirmware = useCallback(async (file: File) => {
    setFirmwareFile(file);
    setResult(null);
    setError(null);

    try {
      const buffer = await file.arrayBuffer();
      const ext = file.name.toLowerCase().split('.').pop();

      let data: Uint8Array;
      if (ext === 'hex' || ext === 'ihx') {
        const text = new TextDecoder().decode(buffer);
        const parsed = parseIntelHex(text);
        data = parsed.data;
      } else if (ext === 'sn8') {
        const parsed = parseSn8(buffer, 0x0000);
        data = parsed.data;
      } else {
        // .bin or unknown — treat as raw binary
        data = new Uint8Array(buffer);
      }

      setFirmwareData(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to parse firmware file');
      setFirmwareData(null);
    }
  }, []);

  const handleFileDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) loadFirmware(file);
  }, [loadFirmware]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) loadFirmware(file);
  }, [loadFirmware]);

  const handleFlashAprom = useCallback(async () => {
    if (!programmer || !selectedChip || !firmwareData) return;
    setOperation('flashing');
    setResult(null);
    setError(null);
    setProgress(null);

    try {
      const res = await engine.flash(programmer, firmwareData, selectedChip, {}, setProgress);
      setResult(res);
      if (!res.success) setError('Flash failed — check verify result');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Flash operation failed');
    } finally {
      setOperation('idle');
    }
  }, [programmer, selectedChip, firmwareData]);

  const handleMassErase = useCallback(async () => {
    if (!programmer) return;
    setOperation('erasing');
    setResult(null);
    setError(null);

    try {
      await programmer.massErase();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Mass erase failed');
    } finally {
      setOperation('idle');
    }
  }, [programmer]);

  const handleVerify = useCallback(async () => {
    if (!programmer || !firmwareData) return;
    setOperation('verifying');
    setResult(null);
    setError(null);
    setProgress(null);

    try {
      const match = await programmer.verifyFlash(0x0000, firmwareData, setProgress);
      if (!match) setError('Verify mismatch');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verify failed');
    } finally {
      setOperation('idle');
    }
  }, [programmer, firmwareData]);

  const handleReadFlash = useCallback(async () => {
    if (!programmer || !selectedChip) return;
    setOperation('reading');
    setError(null);
    setReadData(null);

    try {
      const data = await programmer.readFlash(0x0000, selectedChip.flashSize, setProgress);
      setReadData(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Read flash failed');
    } finally {
      setOperation('idle');
    }
  }, [programmer, selectedChip]);

  const handleDownloadReadback = useCallback(() => {
    if (!readData || !selectedChip) return;
    const blob = new Blob([readData as BlobPart], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedChip.name}_readback.bin`;
    a.click();
    URL.revokeObjectURL(url);
  }, [readData, selectedChip]);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 space-y-4">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
        {t('flash8051.title')}
      </h3>

      {/* File drop zone */}
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleFileDrop}
        className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center hover:border-blue-400 dark:hover:border-blue-500 transition-colors"
      >
        {firmwareFile ? (
          <div className="text-sm">
            <p className="font-medium text-gray-900 dark:text-white">{firmwareFile.name}</p>
            <p className="text-gray-500 dark:text-gray-400">
              {(firmwareFile.size / 1024).toFixed(1)} KB
              {firmwareData && ` (${firmwareData.length} bytes parsed)`}
            </p>
          </div>
        ) : (
          <div className="text-sm text-gray-500 dark:text-gray-400">
            <p>{t('flash8051.dropZone')}</p>
            <p className="text-xs mt-1">{t('flash8051.supportedFormats')}</p>
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept=".hex,.ihx,.bin,.sn8"
          onChange={handleFileSelect}
          className="hidden"
          id="firmware-file-8051"
        />
        <label
          htmlFor="firmware-file-8051"
          className="inline-block mt-2 px-3 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 cursor-pointer hover:underline"
        >
          {t('flash8051.browseFiles')}
        </label>
      </div>

      {/* Progress bar */}
      {progress && isBusy && (
        <div>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-gray-600 dark:text-gray-400">
              {t(`flash.stage.${progress.stage}`)}
            </span>
            <span className="text-gray-400">{progress.percent.toFixed(0)}%</span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div
              className="bg-blue-500 h-2 rounded-full transition-all"
              style={{ width: `${progress.percent}%` }}
            />
          </div>
        </div>
      )}

      {/* Result */}
      {result && (
        <div className={`text-sm p-3 rounded-lg ${result.success
          ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'
          : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'
        }`}>
          <p className="font-medium">
            {result.success ? t('flash.result.success') : t('flash.result.failure')}
          </p>
          <p className="text-xs mt-1">
            {t('flash.result.bytesWritten')}: {result.stages.program.bytesWritten} |{' '}
            {t('flash.result.duration')}: {(result.totalDurationMs / 1000).toFixed(1)}s
          </p>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="text-sm p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Read data download */}
      {readData && (
        <div className="text-sm p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 flex items-center justify-between">
          <span>Read {readData.length} bytes</span>
          <button
            onClick={handleDownloadReadback}
            className="px-3 py-1 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700 transition-colors"
          >
            {t('readback.download')}
          </button>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex flex-wrap gap-3">
        <button
          onClick={handleFlashAprom}
          disabled={!isReady || !firmwareData || isBusy}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          {operation === 'flashing' ? t('flash.stage.program') : t('flash8051.flashAprom')}
        </button>
        <button
          onClick={handleReadFlash}
          disabled={!isReady || isBusy}
          className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 transition-colors"
        >
          {operation === 'reading' ? t('common.loading') : t('flash8051.readFlash')}
        </button>
        <button
          onClick={handleMassErase}
          disabled={!isReady || isBusy}
          className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 transition-colors"
        >
          {operation === 'erasing' ? t('flash.stage.erase') : t('flash8051.massErase')}
        </button>
        <button
          onClick={handleVerify}
          disabled={!isReady || !firmwareData || isBusy}
          className="px-4 py-2 bg-gray-600 text-white rounded-lg text-sm font-medium hover:bg-gray-700 disabled:opacity-50 transition-colors"
        >
          {operation === 'verifying' ? t('flash.stage.verify') : t('flash8051.verify')}
        </button>
      </div>
    </div>
  );
};

export default FlashControls8051;
